import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Group,
  Stack,
  Text,
  Title,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Switch,
  Select,
  Card,
  Alert,
  Code,
  Divider,
  Tooltip,
  MultiSelect,
  ScrollArea,
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCode,
  IconAlertCircle,
  IconCheck,
  IconClock,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

interface ValidationTemplate {
  expr: string;
  async?: boolean;
  params?: string[];
}

interface ValidationTemplatesManagerProps {
  templates?: Record<string, ValidationTemplate>;
  onTemplatesChange: (templates: Record<string, ValidationTemplate>) => void;
  records?: Array<{ record: string; fields: Array<{ xmlTag: string; type: string; description?: string }> }>;
}

interface TemplateFormData {
  name: string;
  expr: string;
  async: boolean;
  params: string[];
  description: string;
}

export const ValidationTemplatesManager: React.FC<ValidationTemplatesManagerProps> = ({
  templates = {},
  onTemplatesChange,
  records = [],
}) => {
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<TemplateFormData>({
    name: '',
    expr: '',
    async: false,
    params: [],
    description: '',
  });
  const [showPreviewModal, setShowPreviewModal] = useState<string | null>(null);


  // Predefined template examples
  const templateExamples = {
    'Portfolio Balance Check': {
      expr: 'await checkBalanceAfterTransaction(portfolio, custodianAccount, -Math.abs(quantity))',
      async: true,
      params: ['portfolio', 'custodianAccount', 'quantity'],
    },
    'Positive Number': {
      expr: 'value > 0',
      async: false,
      params: ['value'],
    },
    'Non-Zero': {
      expr: 'value !== 0',
      async: false,
      params: ['value'],
    },
    'Date Range Valid': {
      expr: 'new Date(startDate) <= new Date(endDate)',
      async: false,
      params: ['startDate', 'endDate'],
    },
    'Valid Currency': {
      expr: "['USD', 'EUR', 'GBP', 'KWD', 'AED', 'SAR'].includes(currency)",
      async: false,
      params: ['currency'],
    },
    'Minimum Amount': {
      expr: 'parseFloat(amount) >= minAmount',
      async: false,
      params: ['amount', 'minAmount'],
    },
    'Business Day': {
      expr: `
        const date = new Date(dateValue);
        const day = date.getDay();
        return day !== 0 && day !== 6; // Not Sunday (0) or Saturday (6)
      `,
      async: false,
      params: ['dateValue'],
    },
  };

  const openEditModal = useCallback((templateName: string | null = null) => {
    if (templateName && templates[templateName]) {
      const template = templates[templateName];
      setFormData({
        name: templateName,
        expr: template.expr,
        async: template.async || false,
        params: template.params || [],
        description: '',
      });
    } else {
      setFormData({
        name: '',
        expr: '',
        async: false,
        params: [],
        description: '',
      });
    }
    setEditingTemplate(templateName || 'new');
    setModalOpen(true);
  }, [templates]);

  const handleSaveTemplate = useCallback(() => {
    if (!formData.name.trim() || !formData.expr.trim()) {
      notifications.show({
        title: 'Error',
        message: 'Template name and expression are required',
        color: 'red',
      });
      return;
    }

    const newTemplate: ValidationTemplate = {
      expr: formData.expr,
      async: formData.async,
      params: formData.params.length > 0 ? formData.params : undefined,
    };

    const updatedTemplates = { ...templates };
    
    // If renaming, delete the old key (but not if it's a new template)
    if (editingTemplate && editingTemplate !== 'new' && editingTemplate !== formData.name) {
      delete updatedTemplates[editingTemplate];
    }
    
    updatedTemplates[formData.name] = newTemplate;
    
    onTemplatesChange(updatedTemplates);
    setEditingTemplate(null);
    setModalOpen(false);
    
    notifications.show({
      title: 'Success',
      message: `Template "${formData.name}" saved successfully`,
      color: 'green',
    });
  }, [formData, templates, onTemplatesChange, editingTemplate]);

  const handleDeleteTemplate = useCallback((templateName: string) => {
    const updatedTemplates = { ...templates };
    delete updatedTemplates[templateName];
    onTemplatesChange(updatedTemplates);
    
    notifications.show({
      title: 'Success',
      message: `Template "${templateName}" deleted`,
      color: 'orange',
    });
  }, [templates, onTemplatesChange]);

  const handleLoadExample = useCallback((exampleName: string) => {
    const example = templateExamples[exampleName as keyof typeof templateExamples];
    if (example) {
      setFormData(prev => ({
        ...prev,
        expr: example.expr,
        async: example.async,
        params: example.params || [],
      }));
    }
  }, []);

  const templatesList = Object.entries(templates).map(([name, template]) => (
    <Card key={name} padding="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Group>
          <Text fw={500}>{name}</Text>
          <Badge color={template.async ? 'blue' : 'gray'} size="sm">
            {template.async ? 'Async' : 'Sync'}
          </Badge>
          {template.params && template.params.length > 0 && (
            <Badge color="violet" size="sm">
              {template.params.length} param{template.params.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Tooltip label="Preview expression">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={() => setShowPreviewModal(name)}
            >
              <IconCode size="1rem" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Edit template">
            <ActionIcon
              variant="light"
              color="yellow"
              onClick={() => openEditModal(name)}
            >
              <IconEdit size="1rem" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Delete template">
            <ActionIcon
              variant="light"
              color="red"
              onClick={() => handleDeleteTemplate(name)}
            >
              <IconTrash size="1rem" />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {template.params && template.params.length > 0 && (
        <Group gap="xs" mb="xs">
          <Text size="sm" c="dimmed">Parameters:</Text>
          {template.params.map(param => (
            <Code key={param}>{param}</Code>
          ))}
        </Group>
      )}

      <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: '100px', overflow: 'auto' }}>
        {template.expr.length > 100 ? `${template.expr.substring(0, 100)}...` : template.expr}
      </Code>
    </Card>
  ));

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={3}>Validation Templates</Title>
          <Text size="sm" c="dimmed">
            Create reusable validation logic that can be applied to fields across records
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size="1rem" />}
          onClick={() => openEditModal()}
        >
          New Template
        </Button>
      </Group>

      {Object.keys(templates).length === 0 ? (
        <Alert color="blue" variant="light" icon={<IconAlertCircle />}>
          <Text>No validation templates defined yet.</Text>
          <Text size="sm" mt="xs">
            Create templates to reuse validation logic across multiple fields and records.
          </Text>
        </Alert>
      ) : (
        <Stack gap="md">
          {templatesList}
        </Stack>
      )}

      {/* Template Editor Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingTemplate(null);
        }}
        title={editingTemplate === 'new' ? 'Create New Template' : `Edit Template: ${editingTemplate}`}
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Template Name"
            placeholder="e.g., portfolioBalanceCheck"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            required
          />

          <Select
            label="Load Example"
            placeholder="Choose a template example to start with"
            data={Object.keys(templateExamples)}
            value={null}
            onChange={(value) => value && handleLoadExample(value)}
            clearable
            searchable
          />

          <Textarea
            label="Expression"
            placeholder="Enter JavaScript expression..."
            value={formData.expr}
            onChange={(e) => setFormData(prev => ({ ...prev, expr: e.target.value }))}
            minRows={3}
            maxRows={10}
            required
            description="Use JavaScript syntax. Access parameters by name."
          />

          <Switch
            label="Async Template"
            description="Enable if this template calls async functions (e.g., Firebase functions)"
            checked={formData.async}
            onChange={(e) => setFormData(prev => ({ ...prev, async: e.currentTarget.checked }))}
          />

          <MultiSelect
            label="Parameters"
            placeholder="Add parameters this template accepts"
            data={[
              'Portfolio', 'LocationAccount', 'Quantity', 'EventDate', "Investment"
            ]}
            value={formData.params}
            onChange={(params) => setFormData(prev => ({ ...prev, params }))}
            searchable
            description="Parameters that fields can pass to this template"
          />

          <Group justify="flex-end">
            <Button variant="outline" onClick={() => {
              setModalOpen(false);
              setEditingTemplate(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>
              {editingTemplate === 'new' ? 'Create' : 'Update'} Template
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Preview Modal */}
      <Modal
        opened={showPreviewModal !== null}
        onClose={() => setShowPreviewModal(null)}
        title={`Template: ${showPreviewModal}`}
        size="md"
      >
        {showPreviewModal && templates[showPreviewModal] && (
          <Stack gap="md">
            <Group>
              <Badge color={templates[showPreviewModal].async ? 'blue' : 'gray'}>
                {templates[showPreviewModal].async ? 'Async' : 'Sync'}
              </Badge>
              {templates[showPreviewModal].params && (
                <Badge color="violet">
                  {templates[showPreviewModal].params!.length} parameters
                </Badge>
              )}
            </Group>

            {templates[showPreviewModal].params && templates[showPreviewModal].params!.length > 0 && (
              <Box>
                <Text fw={500} size="sm" mb="xs">Parameters:</Text>
                <Group gap="xs">
                  {templates[showPreviewModal].params!.map(param => (
                    <Code key={param}>{param}</Code>
                  ))}
                </Group>
              </Box>
            )}

            <Box>
              <Text fw={500} size="sm" mb="xs">Expression:</Text>
              <ScrollArea.Autosize>
                <Code block style={{ whiteSpace: 'pre-wrap' }}>
                  {templates[showPreviewModal].expr}
                </Code>
              </ScrollArea.Autosize>
            </Box>

            <Divider />

            <Box>
              <Text fw={500} size="sm" mb="xs">Usage Example:</Text>
              <Code block>
                {`@${showPreviewModal}(${
                  templates[showPreviewModal].params?.map(param => `${param}=value`).join(', ') || ''
                })`}
              </Code>
            </Box>
          </Stack>
        )}
      </Modal>
    </Box>
  );
};